import type { AppConfig } from './appConfig'
import { miscUiState } from './globalState'

const qsParams = new URLSearchParams(window.location?.search ?? '')

export type AppQsParams = {
  proxy?: string
  username?: string
  lockConnect?: string
  autoConnect?: string
  alwaysReconnect?: string
  // googledrive.ts params
  state?: string
  // Map and texture params
  texturepack?: string
  map?: string
  mapDirBaseUrl?: string
  mapDirGuess?: string
  // Singleplayer params
  singleplayer?: string
  sp?: string
  loadSave?: string
  // Server params
  reconnect?: string
  server?: string
  // Peer connection params
  connectPeer?: string
  peerVersion?: string
  // UI params
  modal?: string
  viewerConnect?: string
  playground?: string
  // Map version param
  mapVersion?: string
  // Command params
  command?: string
  // Misc params
  suggest_save?: string
  noPacketsValidation?: string
  testCrashApp?: string
  freezeSettings?: string
  testIosCrash?: string
  addPing?: string
  parentFrameMods?: string

  // Skin params
  skinApiUrl?: string

  // Replay params
  replayFilter?: string
  replaySpeed?: string
  replayFileUrl?: string
  replayValidateClient?: string
  replayStopOnError?: string
  replaySkipMissingOnTimeout?: string
  replayPacketsSenderDelay?: string
  demoModBlockModel?: string
  demoModBlockFrom?: string
  mockEvents?: string
  demoModBlockMaxDistance?: string

  // Benchmark params
  openBenchmark?: string
  renderDistance?: string
  downloadBenchmark?: string
  benchmarkMapZipUrl?: string
  benchmarkPosition?: string
}

export type AppQsParamsArray = {
  mapDir?: string[]
  setting?: string[]
  serverSetting?: string[]
  command?: string[]
}

type AppQsParamsArrayTransformed = {
  [k in keyof AppQsParamsArray]: string[]
}

globalThis.process ??= {} as any
const initialAppConfig = process?.env?.INLINED_APP_CONFIG as AppConfig ?? {}

export const appQueryParams = new Proxy<AppQsParams>({} as AppQsParams, {
  get (target, property) {
    if (typeof property !== 'string') {
      return undefined
    }
    const qsParam = qsParams.get(property)
    if (qsParam) return qsParam
    return miscUiState.appConfig?.appParams?.[property]
  },
})

export const appQueryParamsArray = new Proxy({} as AppQsParamsArrayTransformed, {
  get (target, property) {
    if (typeof property !== 'string') {
      return null
    }
    const qsParam = qsParams.getAll(property)
    if (qsParam.length) return qsParam
    return miscUiState.appConfig?.appParams?.[property] ?? []
  },
})

let lastQsReplaceTs = 0
let pendingQsReplaceTimeout: number | undefined
let pendingQsReplaceUrl: string | undefined

export function updateQsParam (name: keyof AppQsParams, value: string | undefined) {
  try {
    const url = new URL(window.location.href)
    if (value) {
      url.searchParams.set(name, value)
    } else {
      url.searchParams.delete(name)
    }

    const nextUrl = url.toString()
    if (nextUrl === window.location.href) return

    pendingQsReplaceUrl = nextUrl

    if (pendingQsReplaceTimeout !== undefined) return

    const MIN_INTERVAL_MS = 200
    const delay = Math.max(0, MIN_INTERVAL_MS - (Date.now() - lastQsReplaceTs))
    pendingQsReplaceTimeout = window.setTimeout(() => {
      pendingQsReplaceTimeout = undefined
      const targetUrl = pendingQsReplaceUrl
      pendingQsReplaceUrl = undefined
      if (!targetUrl) return

      try {
        window.history.replaceState({}, '', targetUrl)
        lastQsReplaceTs = Date.now()
      } catch {
      }
    }, delay)
  } catch {
  }
}

// Helper function to check if a specific query parameter exists
export const hasQueryParam = (param: keyof AppQsParams) => qsParams.has(param)

// Helper function to get all query parameters as a URLSearchParams object
export const getRawQueryParams = () => qsParams;

(globalThis as any).debugQueryParams = Object.fromEntries(qsParams.entries())
